import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { Autocomplete, Box, Button, Link, MenuItem, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';

import useBackendApi from '../../../../../hooks/useBackendApi';
import { AccountLocation, AccountStaffMember, EventForm, OrganizationStaffMember } from '../../../../../types/account';
import {
  DateOnlyPicker,
  TimeSelect,
  extractDate,
  extractTime,
  combineDateTime,
} from '../../../../../components/common/CustomDateTimePicker';
import { fetchOrganizationStaffData } from '../../../OrganizationStaff';
import { fetchAccountStaff } from '../AccountStaff';
import { fetchAccountLocations } from '../locations/AccountLocations';
import LocationDrawer, { LocationDrawerPayload } from '../locations/LocationDrawer';
import { filterAssignableOrgStaff } from './roleUtils';
import { fetchAccountEventAssignedStaff, fetchAccountEventForms } from './eventSupportApi';

export type AccountEventFormValues = {
  title: string;
  event_type: string;
  event_start_date: string;
  event_end_date: string;
  location_id: string;
  description: string;
  notes: string;
  guests: string[];
  assigned_staff_ids: number[];
};

const DEFAULT_FORM_VALUES: AccountEventFormValues = {
  title: '',
  event_type: 'job',
  event_start_date: '',
  event_end_date: '',
  location_id: '',
  description: '',
  notes: '',
  guests: [],
  assigned_staff_ids: [],
};

const backendURL = process.env.REACT_APP_BACKEND_URL as string;

interface AccountEventFormProps {
  mode: 'create' | 'edit';
  initialEventId?: number | null;
  initialValues?: Partial<AccountEventFormValues>;
  orgPathName: string;
  accPathName: string;
  allowStaffAssignment: boolean;
  showStaffAssignment?: boolean;
  onValuesChange: (values: AccountEventFormValues) => void;
  onValidationChange?: (isValid: boolean) => void;
  onSupportDataLoadingChange?: (isLoading: boolean) => void;
  isDirty?: (dirty: boolean) => void;
  isTitleReadOnly?: boolean;
}

type DateErrors = {
  startDate?: string;
  startTime?: string;
  endDate?: string;
  endTime?: string;
  range?: string;
};

type SupportDataStatus = 'idle' | 'loading' | 'loaded' | 'error';

const hasAllowedRole = (roles: string[], allowedRoles: string[]): boolean =>
  roles.some((role) => allowedRoles.includes((role || '').toLowerCase()));

export default function AccountEventForm({
  mode,
  initialEventId = null,
  initialValues,
  orgPathName,
  accPathName,
  allowStaffAssignment,
  showStaffAssignment,
  onValuesChange,
  onValidationChange,
  onSupportDataLoadingChange,
  isDirty,
  isTitleReadOnly = false,
}: AccountEventFormProps): JSX.Element {
  const [formValues, setFormValues] = useState<AccountEventFormValues>(DEFAULT_FORM_VALUES);
  const { apiFetchRequest } = useBackendApi();
  const navigate = useNavigate();

  const [startDate, setStartDate] = useState<string | null>(null);
  const [startTime, setStartTime] = useState<string>('09:00');
  const [endDate, setEndDate] = useState<string | null>(null);
  const [endTime, setEndTime] = useState<string>('10:00');

  const [dateErrors, setDateErrors] = useState<DateErrors>({});
  const [locations, setLocations] = useState<AccountLocation[]>([]);
  const [userOrgRoles, setUserOrgRoles] = useState<string[]>([]);
  const [userAccRoles, setUserAccRoles] = useState<string[]>([]);
  const [staffOptions, setStaffOptions] = useState<Array<{ email: string; label: string; name?: string | null }>>([]);
  const [orgStaff, setOrgStaff] = useState<OrganizationStaffMember[]>([]);
  const [formOptions, setFormOptions] = useState<EventForm[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [isLocationDrawerOpen, setIsLocationDrawerOpen] = useState(false);
  const [isSubmittingLocation, setIsSubmittingLocation] = useState(false);
  const [staffLoading, setStaffLoading] = useState(false);
  const [orgStaffLoading, setOrgStaffLoading] = useState(false);
  const [formOptionsLoading, setFormOptionsLoading] = useState(false);
  const [assignedStaffSupportStatus, setAssignedStaffSupportStatus] = useState<SupportDataStatus>('idle');
  const [dataError, setDataError] = useState<string | null>(null);
  const hasFetchedBaseOptionsRef = useRef(false);
  const hasFetchedOrgStaffRef = useRef(false);
  const shouldShowAssignStaff = showStaffAssignment ?? allowStaffAssignment;
  const shouldLoadAssignStaff = allowStaffAssignment;
  const shouldFetchAssignedStaffSupport =
    mode === 'edit' &&
    initialEventId !== null &&
    shouldLoadAssignStaff &&
    !Array.isArray(initialValues?.assigned_staff_ids);
  const isAssignedStaffSupportLoading = shouldFetchAssignedStaffSupport && assignedStaffSupportStatus !== 'loaded';
  const isSupportDataLoading = isAssignedStaffSupportLoading;
  const canAddLocations =
    mode === 'create' && (hasAllowedRole(userOrgRoles, ['admin']) || hasAllowedRole(userAccRoles, ['admin', 'member']));

  // Initialize values
  useEffect(() => {
    setFormValues({
      ...DEFAULT_FORM_VALUES,
      ...initialValues,
      assigned_staff_ids: initialValues?.assigned_staff_ids ?? DEFAULT_FORM_VALUES.assigned_staff_ids,
    });

    const es = initialValues?.event_start_date ?? '';
    const ee = initialValues?.event_end_date ?? '';
    setStartDate(extractDate(es) || (mode === 'create' ? dayjs().format('YYYY-MM-DD') : null));
    setStartTime(extractTime(es) || '09:00');
    setEndDate(extractDate(ee) || (mode === 'create' ? dayjs().format('YYYY-MM-DD') : null));
    setEndTime(extractTime(ee) || '10:00');
  }, [initialValues, mode]);

  const fetchLocationsForField = useCallback(async (): Promise<AccountLocation[] | null> => {
    setLocationsLoading(true);

    try {
      const locRes = await fetchAccountLocations({ apiFetchRequest, backendURL, orgPathName, accPathName });

      if (!locRes) {
        setDataError('Unable to load locations.');
        return null;
      }

      if (locRes.unauthorized) {
        navigate('/401.html');
        return null;
      }

      if (locRes.notFound) {
        navigate('/404.html');
        return null;
      }

      if (locRes.isError || locRes.body?.message !== 'SUCCESS') {
        setDataError('Unable to load locations.');
        return null;
      }

      const nextLocations = Array.isArray(locRes.body?.locations) ? locRes.body.locations : [];
      setLocations(nextLocations);
      setUserOrgRoles(Array.isArray(locRes.body?.user_org_roles) ? locRes.body.user_org_roles : []);
      setUserAccRoles(Array.isArray(locRes.body?.user_acc_roles) ? locRes.body.user_acc_roles : []);
      return nextLocations;
    } finally {
      setLocationsLoading(false);
    }
  }, [accPathName, apiFetchRequest, navigate, orgPathName]);

  // Async data fetching
  useEffect(() => {
    const shouldFetchBaseOptions = !hasFetchedBaseOptionsRef.current;
    const shouldFetchOrgStaff = shouldLoadAssignStaff && !hasFetchedOrgStaffRef.current;

    if (!shouldFetchBaseOptions && !shouldFetchOrgStaff) return;

    if (shouldFetchBaseOptions) hasFetchedBaseOptionsRef.current = true;
    if (shouldFetchOrgStaff) hasFetchedOrgStaffRef.current = true;

    const fetchAll = async () => {
      if (shouldFetchBaseOptions) {
        setStaffLoading(true);
        setFormOptionsLoading(true);
      }
      if (shouldFetchOrgStaff) setOrgStaffLoading(true);

      try {
        if (shouldFetchBaseOptions) {
          const [, staffRes, forms] = await Promise.all([
            fetchLocationsForField(),
            fetchAccountStaff({ apiFetchRequest, backendURL, orgPathName, accPathName }),
            fetchAccountEventForms({
              accPathName,
              apiFetchRequest,
              eventId: mode === 'edit' ? initialEventId : null,
              fail: () => setDataError('Unable to load forms.'),
              navigate,
              orgPathName,
            }),
          ]);

          if (staffRes && !staffRes.isError && staffRes.body?.message === 'SUCCESS') {
            const map = new Map();
            (staffRes.body.staff || []).forEach((member: AccountStaffMember) => {
              const email = (member?.email || '').trim();
              if (!email) return;
              const name = member?.full_name || [member?.first_name, member?.last_name].filter(Boolean).join(' ');
              map.set(email.toLowerCase(), { email, label: name ? `${name} (${email})` : email, name });
            });
            setStaffOptions(Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label)));
          }

          if (forms) setFormOptions(forms);
        }

        if (shouldFetchOrgStaff) {
          const orgRes = await fetchOrganizationStaffData(orgPathName, apiFetchRequest);
          if (orgRes && !orgRes.isError) setOrgStaff(orgRes.body?.staff || []);
        }
      } catch (err) {
        setDataError('Unable to load some form options.');
      } finally {
        if (shouldFetchBaseOptions) {
          setLocationsLoading(false);
          setStaffLoading(false);
          setFormOptionsLoading(false);
        }
        if (shouldFetchOrgStaff) setOrgStaffLoading(false);
      }
    };

    fetchAll();
  }, [accPathName, apiFetchRequest, fetchLocationsForField, orgPathName, shouldLoadAssignStaff]);

  useEffect(() => {
    onSupportDataLoadingChange?.(isSupportDataLoading);
  }, [isSupportDataLoading, onSupportDataLoadingChange]);

  // Fetch assigned staff in edit mode if not provided
  useEffect(() => {
    if (!shouldFetchAssignedStaffSupport) {
      setAssignedStaffSupportStatus('idle');
      return;
    }

    if (initialEventId === null) return;

    let cancelled = false;
    setAssignedStaffSupportStatus('loading');

    fetchAccountEventAssignedStaff({
      accPathName,
      apiFetchRequest,
      eventId: initialEventId,
      fail: () => setDataError('Unable to load assigned staff.'),
      navigate,
      orgPathName,
    }).then((staff) => {
      if (cancelled) return;

      if (staff) {
        const ids = staff.map((s) => s.org_staff_id).filter((id): id is number => typeof id === 'number');
        setFormValues((prev) => ({ ...prev, assigned_staff_ids: ids }));
        setAssignedStaffSupportStatus('loaded');
      } else {
        setAssignedStaffSupportStatus('error');
      }
    });

    return () => {
      cancelled = true;
    };
  }, [accPathName, apiFetchRequest, initialEventId, navigate, orgPathName, shouldFetchAssignedStaffSupport]);

  // Validation and reporting back
  const validate = useCallback(() => {
    const errors: DateErrors = {};
    const startIso = combineDateTime(startDate, startTime) ?? '';
    const endIso = combineDateTime(endDate, endTime) ?? '';

    if (!startDate) errors.startDate = 'Start date is required.';
    if (!endDate) errors.endDate = 'End date is required.';

    if (startIso && endIso) {
      const start = dayjs(startIso);
      const end = dayjs(endIso);
      if (end.isSame(start) || end.isBefore(start)) {
        errors.range = 'End date/time must be after start date/time.';
      }
    }

    setDateErrors(errors);
    const isValid = !!formValues.title && Object.keys(errors).length === 0;
    onValidationChange?.(isValid);

    onValuesChange({
      ...formValues,
      event_start_date: startIso,
      event_end_date: endIso,
    });
  }, [startDate, startTime, endDate, endTime, formValues, onValidationChange, onValuesChange]);

  useEffect(() => {
    validate();
  }, [validate]);

  // Form Handlers
  const handleFieldChange =
    (field: keyof AccountEventFormValues) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormValues((prev) => ({ ...prev, [field]: event.target.value }));
      isDirty?.(true);
    };

  const handleStartDateChange = (value: string | null) => {
    setStartDate(value);
    if (mode === 'create') setEndDate(value);
    isDirty?.(true);
  };

  const staffOptionsMap = useMemo(() => {
    const map = new Map();
    staffOptions.forEach((o) => map.set(o.email.toLowerCase(), o));
    return map;
  }, [staffOptions]);

  const assignableOrgStaff = useMemo(() => filterAssignableOrgStaff(orgStaff), [orgStaff]);
  const selectedAssignedStaff = useMemo(() => {
    if (!formValues.assigned_staff_ids.length) return [];

    const ids = new Set(formValues.assigned_staff_ids);
    return assignableOrgStaff.filter((member) => ids.has(member.org_staff_id));
  }, [assignableOrgStaff, formValues.assigned_staff_ids]);

  const getStaffLabel = useCallback((member: OrganizationStaffMember) => {
    const name =
      member.full_name ||
      [member.first_name, member.last_name]
        .filter((part) => (part || '').trim())
        .join(' ')
        .trim();
    const email = (member.email || '').trim();

    if (name && email) return `${name} (${email})`;
    return name || email || 'Staff';
  }, []);

  const handleOpenLocationDrawer = useCallback(() => {
    if (!canAddLocations) return;
    setDataError(null);
    setIsLocationDrawerOpen(true);
  }, [canAddLocations]);

  const handleCloseLocationDrawer = useCallback(() => {
    if (isSubmittingLocation) return;
    setIsLocationDrawerOpen(false);
  }, [isSubmittingLocation]);

  const handleCreateLocation = useCallback(
    async (payload: LocationDrawerPayload) => {
      if (!canAddLocations) return;

      setIsSubmittingLocation(true);
      setDataError(null);

      try {
        const res = await apiFetchRequest({
          endpoint: `${backendURL}/o/${encodeURIComponent(orgPathName)}/a/${encodeURIComponent(accPathName)}/locations/`,
          method: 'POST',
          body: payload,
        });

        if (!res) {
          throw new Error('Unable to create location.');
        }

        const body = res.body as {
          message?: string;
          detail?: string;
          status_code?: number;
          acc_location_id?: number;
        };

        if (
          res.status === 401 ||
          body?.status_code === 401 ||
          body?.message === 'UNAUTHENTICATED' ||
          body?.message === 'UNAUTHORIZED' ||
          body?.message === 'NO_ACCESS'
        ) {
          navigate('/401.html');
          return;
        }

        if (res.status === 404 || body?.status_code === 404 || body?.message === 'NOT_FOUND') {
          navigate('/404.html');
          return;
        }

        if (res.isError || body?.message !== 'SUCCESS' || !body?.acc_location_id) {
          throw new Error(body?.detail || 'Unable to create location.');
        }

        await fetchLocationsForField();
        setFormValues((prev) => ({ ...prev, location_id: String(body.acc_location_id) }));
        isDirty?.(true);
        setIsLocationDrawerOpen(false);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unable to create location.';
        setDataError(message);
        throw error;
      } finally {
        setIsSubmittingLocation(false);
      }
    },
    [accPathName, apiFetchRequest, canAddLocations, fetchLocationsForField, isDirty, navigate, orgPathName],
  );

  return (
    <Box sx={{ display: 'grid', gap: 2.5 }}>
      {dataError && (
        <Typography variant="body2" color="error">
          {dataError}
        </Typography>
      )}

      <TextField
        label="Title"
        value={formValues.title}
        onChange={handleFieldChange('title')}
        required
        fullWidth
        disabled={isTitleReadOnly}
        InputProps={{
          readOnly: isTitleReadOnly,
        }}
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1.2fr 0.9fr auto 0.9fr 1.2fr' },
          gap: 1,
          alignItems: 'center',
        }}
      >
        <DateOnlyPicker label="Start" value={startDate} onChange={handleStartDateChange} />
        <TimeSelect
          label="Start time"
          value={startTime}
          onChange={(v) => {
            setStartTime(v);
            isDirty?.(true);
          }}
        />
        <Typography variant="body2" sx={{ px: 0.5, color: 'text.secondary' }}>
          to
        </Typography>
        <TimeSelect
          label="End time"
          value={endTime}
          onChange={(v) => {
            setEndTime(v);
            isDirty?.(true);
          }}
        />
        <DateOnlyPicker
          label="End"
          value={endDate}
          onChange={(v) => {
            setEndDate(v);
            isDirty?.(true);
          }}
        />
      </Box>

      {Object.values(dateErrors).map((err, i) => (
        <Typography key={i} variant="caption" color="error" display="block">
          {err}
        </Typography>
      ))}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: canAddLocations ? 'minmax(0, 1fr) auto' : '1fr' },
          gap: 1,
          alignItems: 'flex-start',
        }}
      >
        <TextField
          label="Location"
          select
          value={formValues.location_id}
          onChange={handleFieldChange('location_id')}
          fullWidth
          disabled={locationsLoading}
          InputLabelProps={{ shrink: true }}
          SelectProps={{ displayEmpty: true }}
          sx={{
            '& .MuiSelect-select': {
              color: formValues.location_id ? 'inherit' : 'text.secondary',
            },
          }}
        >
          <MenuItem value="">Select location</MenuItem>
          {locations.map((loc) => (
            <MenuItem key={loc.acc_location_id} value={String(loc.acc_location_id)}>
              {loc.name}
            </MenuItem>
          ))}
        </TextField>

        {canAddLocations ? (
          <Button
            variant="outlined"
            startIcon={<AddIcon fontSize="small" />}
            onClick={handleOpenLocationDrawer}
            disabled={locationsLoading}
            sx={{
              height: 56,
              minWidth: { xs: '100%', sm: 118 },
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: 700,
              letterSpacing: 0,
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
            }}
          >
            Add New
          </Button>
        ) : null}
      </Box>

      <TextField
        label="Description"
        value={formValues.description}
        onChange={handleFieldChange('description')}
        fullWidth
        multiline
        minRows={3}
      />

      <TextField
        label="Notes"
        value={formValues.notes}
        onChange={handleFieldChange('notes')}
        fullWidth
        multiline
        minRows={2}
      />

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
          Configured Questionnaires
        </Typography>
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
          <Box
            sx={{ display: 'flex', bgcolor: 'action.hover', p: 1, borderBottom: '1px solid', borderColor: 'divider' }}
          >
            <Typography variant="caption" sx={{ flex: 2, fontWeight: 700 }}>
              NAME
            </Typography>
            <Typography variant="caption" sx={{ flex: 1, fontWeight: 700 }}>
              TYPE
            </Typography>
          </Box>
          {formOptionsLoading ? (
            <Box sx={{ display: 'flex', p: 1, alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                Loading forms...
              </Typography>
            </Box>
          ) : formOptions.length > 0 ? (
            formOptions.map((form) => (
              <Box key={form.form_id} sx={{ display: 'flex', p: 1, alignItems: 'center' }}>
                <Typography variant="body2" sx={{ flex: 2 }}>
                  {form.url ? (
                    <Link href={form.url} target="_blank" rel="noopener noreferrer" underline="hover">
                      {form.name}
                    </Link>
                  ) : (
                    form.name
                  )}
                </Typography>
                <Typography variant="body2" sx={{ flex: 1 }}>
                  Form
                </Typography>
              </Box>
            ))
          ) : (
            <Box sx={{ display: 'flex', p: 1, alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                No forms configured.
              </Typography>
            </Box>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          * Forms are shown based on the organization&apos;s compliance configuration.
        </Typography>
      </Box>

      {shouldShowAssignStaff && (
        <Autocomplete
          multiple
          options={assignableOrgStaff}
          getOptionLabel={getStaffLabel}
          value={selectedAssignedStaff}
          onChange={(_, val) => {
            const ids = Array.from(new Set(val.map((v) => v.org_staff_id).filter((id) => typeof id === 'number')));
            setFormValues((prev) => ({ ...prev, assigned_staff_ids: ids }));
            isDirty?.(true);
          }}
          isOptionEqualToValue={(option, value) => option.org_staff_id === value.org_staff_id}
          loading={orgStaffLoading || isAssignedStaffSupportLoading}
          disabled={!allowStaffAssignment || orgStaffLoading || isAssignedStaffSupportLoading}
          openOnFocus
          filterSelectedOptions
          noOptionsText={orgStaffLoading || isAssignedStaffSupportLoading ? 'Loading staff...' : 'No staff available'}
          renderInput={(params) => <TextField {...params} label="Assigned Staff" />}
        />
      )}

      <Autocomplete
        multiple
        options={staffOptions}
        getOptionLabel={(o) => o.label || o.email}
        value={formValues.guests.map((email) => staffOptionsMap.get(email.toLowerCase()) || { email, label: email })}
        onChange={(_, val) => {
          setFormValues((prev) => ({ ...prev, guests: val.map((v) => v.email) }));
          isDirty?.(true);
        }}
        loading={staffLoading}
        disabled={staffLoading}
        renderInput={(params) => <TextField {...params} label="Attendees" />}
      />

      <LocationDrawer
        open={isLocationDrawerOpen}
        mode="create"
        onClose={handleCloseLocationDrawer}
        onSubmit={handleCreateLocation}
        isSubmitting={isSubmittingLocation}
      />
    </Box>
  );
}
